import { GetStaticPaths, GetStaticProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../assets/theme';
import LandingPageLayout from '../../../components/layouts/LandingPageLayout';
import useAuthGuardPrompt from '../../../hooks/useAuthGuardPrompt';
import { DefaultLocale } from '../../../providers/tolgee';
import { getBaseUrl } from '../../../utils/client';

// Stable og:image URL served from /public (see hub page comment).
const OG_IMAGE_PATH = '/phonics-wizard-og.png';
import {
  CURRICULA,
  CURRICULUM_MARKETING,
  CurriculumDetailCopy,
  CurriculumEntry,
  CurriculumMarketing,
  extractRuleFromValue,
  formatGradeLabel,
  getCurriculumBySlug,
  getCurriculumDetailCopy,
  getObjectivesForCurriculum,
  LandingObjective,
} from '../../../utils/phonicsLandingPages';

/**
 * Build the post-signup / post-login redirect URL for the teacher-facing
 * "Use this in your classroom" CTA. Carries the curriculum slug so the
 * Phonics Wizard form opens with that scope pre-selected — same
 * deep-link mechanic as the per-objective page, just without a specific
 * objective. The `<a href>` points at this URL; the click handler
 * intercepts to send authenticated users via router.push and anonymous
 * users through the login/signup modal.
 */
function buildCurriculumLessonRedirect(curriculumSlug: string): string {
  const params = new URLSearchParams({
    selectedCardId: 'activity-phonics-wizard',
    phonicsCurriculum: curriculumSlug,
  });
  return `/?${params.toString()}`;
}
// Curriculum-admin audience: "Book a demo" always lands on the existing
// quotes page (matches the hub-page convention) rather than running people
// through the teacher signup → Phonics Wizard flow.
const BOOK_A_DEMO_HREF = '/quotes';

type SerializableObjective = Omit<LandingObjective, 'unitNumber'> & {
  unitNumber: number | null;
};

type RecommendedCurriculum = {
  slug: string;
  displayName: string;
  lessonCount: string;
  description: string;
};

type GradeChip = {
  grade: string;
  label: string;
  count: number;
};

interface Props {
  curriculum: CurriculumEntry;
  marketing: CurriculumMarketing;
  detail: CurriculumDetailCopy;
  sampleObjectives: SerializableObjective[];
  gradeChips: GradeChip[];
  recommended: RecommendedCurriculum[];
}

const CurriculumPhonicsPage = ({
  curriculum,
  marketing,
  detail,
  sampleObjectives,
  gradeChips,
  recommended,
}: Props) => {
  const [activeGrade, setActiveGrade] = useState<string>('all');

  // Browse-section "Use this in your classroom" CTA. Same session-aware
  // model as the per-objective page: authenticated users router.push
  // straight into the Phonics Wizard with the curriculum pre-selected,
  // anonymous users see the shared login/signup modal whose callback
  // routes back to the same URL, and `loading` falls through to the
  // server-side session check on `/`.
  const { status: sessionStatus } = useSession();
  const router = useRouter();
  const curriculumLessonRedirect = buildCurriculumLessonRedirect(curriculum.slug);
  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    undefined,
    curriculumLessonRedirect,
  );
  const handleUseInClassroom = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    if (sessionStatus === 'loading') return;
    if (sessionStatus === 'authenticated') {
      e.preventDefault();
      router.push(curriculumLessonRedirect);
      return;
    }
    e.preventDefault();
    showAuthGuard();
  };

  // If the teacher has a grade saved from the home page (the shared
  // `@curipod_v1_home_grade` key) and that grade matches one of the
  // available chips on this curriculum, auto-select it. Falls back to
  // "All grades" when there's no match or no stored value.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const data = window.localStorage.getItem('@curipod_v1_home_grade');
      if (!data) return;
      const parsed = JSON.parse(data) as { value?: string };
      const storedGrade = parsed?.value;
      if (typeof storedGrade !== 'string') return;
      const match = gradeChips.find(
        chip => chip.grade !== 'all' && chip.grade === storedGrade,
      );
      if (match) setActiveGrade(match.grade);
    } catch {
      /* ignore parse errors — fall back to "All grades" */
    }
  }, [gradeChips]);

  const visibleObjectives = useMemo(() => {
    if (activeGrade === 'all') return sampleObjectives;
    return sampleObjectives.filter(o => o.grade === activeGrade);
  }, [sampleObjectives, activeGrade]);

  const activeChip = gradeChips.find(c => c.grade === activeGrade);
  const browseNote =
    activeGrade === 'all'
      ? `Showing all ${visibleObjectives.length} activities mapped to ${curriculum.displayName}. Filter by grade to narrow down.`
      : `Showing all ${visibleObjectives.length} ${activeChip?.label ?? ''} activities.`;

  return (
    <LandingPageLayout
      title={`Phonics Practice · ${curriculum.displayName} — Curipod`}
      ogTitle={`Phonics Practice aligned to ${curriculum.displayName}`}
      subTitle={`${marketing.lessonCount} phonics encoding activities aligned to ${curriculum.displayName}'s scope and sequence.`}
      imageUrl={`${getBaseUrl()}${OG_IMAGE_PATH}`}
      disableHeadline
    >
      <Hero>
        <HeroGrid>
          <HeroText>
            <Breadcrumbs>
              <Link href="/phonics-practice#curricula" passHref>
                <a>Phonics practice</a>
              </Link>
              <span aria-hidden="true">/</span>
              <span>{curriculum.displayName}</span>
            </Breadcrumbs>
            <HeroH1>
              Phonics practice aligned to
              <br />
              <HeroAccent $brand={marketing.brand.accent}>
                {curriculum.displayName}.
              </HeroAccent>
            </HeroH1>
            <HeroLede>
              {marketing.lessonCount} phonics encoding activities aligned to{' '}
              {curriculum.displayName}&apos;s scope and sequence.
            </HeroLede>
            <HeroDivider />
            <InlineStats>
              <InlineStat>
                <InlineStatN $brand={marketing.brand.accent}>
                  {marketing.lessonCount}
                </InlineStatN>
                <InlineStatL>Phonics lessons</InlineStatL>
              </InlineStat>
              <InlineStat>
                <InlineStatN $brand={marketing.brand.accent}>
                  {marketing.gradeRange}
                </InlineStatN>
                <InlineStatL>Grade coverage</InlineStatL>
              </InlineStat>
              <InlineStat>
                <InlineStatN $brand={marketing.brand.accent}>
                  {marketing.alignmentClaim}
                </InlineStatN>
                <InlineStatL>{marketing.alignmentStatLabel}</InlineStatL>
              </InlineStat>
            </InlineStats>
            <HeroCtas>
              <PrimaryButton as="a" href={BOOK_A_DEMO_HREF}>
                Book a demo
              </PrimaryButton>
              <GhostButton as="a" href="#browse">
                See all {marketing.lessonCount} lessons
              </GhostButton>
            </HeroCtas>
          </HeroText>

          <AlignmentCard>
            <Eyebrow>Phonics alignment</Eyebrow>
            <AlignmentCardTitle>
              Mapped to {curriculum.displayName}&apos;s phonics scope
            </AlignmentCardTitle>
            <AlignmentList>
              <AlignmentRow>
                <span>Every target sound</span>
                <AlignmentCheck $brand={marketing.brand.accent}>✓</AlignmentCheck>
              </AlignmentRow>
              {detail.alignmentChips.map(chip => (
                <AlignmentRow key={chip}>
                  <span>{chip}</span>
                  <AlignmentCheck $brand={marketing.brand.accent}>✓</AlignmentCheck>
                </AlignmentRow>
              ))}
            </AlignmentList>
          </AlignmentCard>
        </HeroGrid>
      </Hero>

      <Section>
        <ScopeGrid>
          <ScopeBody>
            <Eyebrow>About the phonics scope</Eyebrow>
            <SecH2 $align="left" $small>
              {detail.scope.title}
            </SecH2>
            {detail.scope.paragraphs.map((p, i) => (
              <ScopeP key={i}>{p}</ScopeP>
            ))}
            <ScopeQuote>&ldquo;{detail.scope.quote}&rdquo;</ScopeQuote>
          </ScopeBody>
          <ScopeMeta>
            {detail.scope.meta.map(row => (
              <SmRow key={row.key}>
                <SmKey>{row.key}</SmKey>
                <SmValue>{row.value}</SmValue>
              </SmRow>
            ))}
          </ScopeMeta>
        </ScopeGrid>
      </Section>

      <SectionWhite>
        <Wrap>
          <SecHead>
            <Eyebrow>How Curipod fits</Eyebrow>
            <SecH2>
              Whole-class phonics practice, dropped into your {curriculum.displayName}{' '}
              lesson.
            </SecH2>
            <SecLead>
              Rigorous, engaging practice, together. Aligned to the science of reading,
              and complementing — not replacing — {curriculum.displayName}&apos;s direct
              instruction.
            </SecLead>
          </SecHead>
          <Steps>
            <Step>
              <StepNum>1</StepNum>
              <StepH3>Hear the sound</StepH3>
              <StepP>
                Teacher introduces the target grapheme from the {curriculum.displayName}{' '}
                lesson. Students listen and identify.
              </StepP>
            </Step>
            <Step>
              <StepNum>2</StepNum>
              <StepH3>Spell &amp; get feedback</StepH3>
              <StepP>
                Every student writes words with the target sound. Each one gets instant
                feedback and a score.
              </StepP>
            </Step>
            <Step>
              <StepNum>3</StepNum>
              <StepH3>Read aloud</StepH3>
              <StepP>Students share their words with a partner or the whole class.</StepP>
            </Step>
            <Step>
              <StepNum>4</StepNum>
              <StepH3>Learn &amp; try again</StepH3>
              <StepP>
                Teacher sees who got it. Students hear new words from peers — then try
                again.
              </StepP>
            </Step>
          </Steps>
        </Wrap>
      </SectionWhite>

      <Band>
        <BandGrid>
          {detail.credibility.map((card, i) => (
            <BandCard key={i}>
              <BandIcon $variant={i === 0 ? 'check' : 'arrow'}>
                {i === 0 ? '✓' : '→'}
              </BandIcon>
              <BandH3>{card.title}</BandH3>
              <BandP>{card.body}</BandP>
            </BandCard>
          ))}
        </BandGrid>
      </Band>

      <Section id="browse">
        <Wrap>
          <SecHead>
            <Eyebrow>Browse lessons</Eyebrow>
            <SecH2>Phonics practice for every {curriculum.displayName} lesson.</SecH2>
            <SecLead>
              Filter by grade. Click any lesson to open the activity in Curipod.
            </SecLead>
          </SecHead>

          <GradeFilter>
            {gradeChips.map(chip => (
              <GChip
                key={chip.grade}
                $active={activeGrade === chip.grade}
                onClick={() => setActiveGrade(chip.grade)}
                type="button"
              >
                {chip.label} <GChipN>{chip.count}</GChipN>
              </GChip>
            ))}
          </GradeFilter>

          <LessonGrid>
            {visibleObjectives.length === 0 ? (
              <LessonEmpty>
                More {activeChip?.label.toLowerCase()} lessons inside Curipod — see all{' '}
                {marketing.lessonCount} to browse them.
              </LessonEmpty>
            ) : (
              visibleObjectives.map(obj => (
                <Link
                  key={obj.id}
                  href={`/phonics-practice/${curriculum.slug}/${obj.slug}`}
                  passHref
                >
                  <LessonCard>
                    <LcTag>
                      {formatGradeLabel(obj.grade)}
                      {obj.unitNumber !== null ? ` · Unit ${obj.unitNumber}` : ''}
                      {' · '}Lesson {obj.lessonNumber}
                    </LcTag>
                    <LcTitle>{obj.value}</LcTitle>
                    <LcLine>
                      Target sound: <strong>{extractRuleFromValue(obj.value)}</strong>
                    </LcLine>
                    {obj.exampleWords && (
                      <LcWords>
                        {obj.exampleWords.map(w => (
                          <LcWord key={w}>{w}</LcWord>
                        ))}
                      </LcWords>
                    )}
                    <LcFoot>
                      <LcGradeBadge>{formatGradeLabel(obj.grade)}</LcGradeBadge>
                      <LcOpen>Open activity →</LcOpen>
                    </LcFoot>
                  </LessonCard>
                </Link>
              ))
            )}
          </LessonGrid>

          <BrowseNote>{browseNote}</BrowseNote>
          <BrowseCta>
            <PrimaryButton
              as="a"
              href={curriculumLessonRedirect}
              onClick={handleUseInClassroom}
            >
              Use this in your classroom
            </PrimaryButton>
          </BrowseCta>
        </Wrap>
      </Section>

      <SectionWhite>
        <Wrap>
          <SecHead>
            <Eyebrow>Common questions</Eyebrow>
            <SecH2>About Curipod phonics and {curriculum.displayName}.</SecH2>
          </SecHead>
          <Faq>
            {detail.faq.map((item, i) => (
              <FaqItem
                key={item.q}
                question={item.q}
                answer={item.a}
                defaultOpen={i === 0}
              />
            ))}
          </Faq>
        </Wrap>
      </SectionWhite>

      <Section>
        <Wrap>
          <SecHead>
            <Eyebrow>Other curricula we cover</Eyebrow>
            <SecH2>Teaching with something else? We&apos;re aligned to that too.</SecH2>
          </SecHead>
          <MiniGrid>
            {recommended.map(r => (
              <Link key={r.slug} href={`/phonics-practice/${r.slug}`} passHref>
                <Mini>
                  <MiniH3>{r.displayName}</MiniH3>
                  <MiniN>{r.lessonCount} phonics lessons</MiniN>
                  <MiniP>{r.description}</MiniP>
                </Mini>
              </Link>
            ))}
          </MiniGrid>
        </Wrap>
      </Section>
      {authGuardComponent}
    </LandingPageLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: CURRICULA.map(c => ({ params: { curriculum: c.slug } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props> = async ctx => {
  const slug = ctx.params?.curriculum as string;
  const curriculum = getCurriculumBySlug(slug);
  if (!curriculum) return { notFound: true };

  const marketing = CURRICULUM_MARKETING[curriculum.value];
  if (!marketing) return { notFound: true };

  const detail = getCurriculumDetailCopy(curriculum);
  const objectives = getObjectivesForCurriculum(curriculum.value);

  const totalsByGrade = new Map<string, number>();
  for (const obj of objectives) {
    totalsByGrade.set(obj.grade, (totalsByGrade.get(obj.grade) ?? 0) + 1);
  }
  const orderedGrades: string[] = [];
  for (const obj of objectives) {
    if (!orderedGrades.includes(obj.grade)) orderedGrades.push(obj.grade);
  }

  const gradeChips: GradeChip[] = [
    { grade: 'all', label: 'All grades', count: objectives.length },
    ...orderedGrades.map(grade => ({
      grade,
      label: formatGradeLabel(grade),
      count: totalsByGrade.get(grade) ?? 0,
    })),
  ];

  // Render every non-disabled objective on the page. This is intentional
  // for SEO: each card is an indexable link to a per-objective page, so
  // we want all of them on the curriculum page. The grade-filter chips
  // hide/show client-side via React state.
  const sampleObjectives: SerializableObjective[] = objectives.map(o => ({
    ...o,
    unitNumber: o.unitNumber ?? null,
  }));

  const recommended: RecommendedCurriculum[] = detail.otherCurriculumSlugs
    .map(otherSlug => {
      const other = CURRICULA.find(c => c.slug === otherSlug);
      const m = other && CURRICULUM_MARKETING[other.value];
      if (!other || !m) return null;
      return {
        slug: other.slug,
        displayName: other.displayName,
        lessonCount: m.lessonCount,
        description: m.description,
      };
    })
    .filter((x): x is RecommendedCurriculum => x !== null);

  return {
    props: ({
      curriculum,
      marketing,
      detail,
      sampleObjectives,
      gradeChips,
      recommended,
      _tolgeeSSR: { language: DefaultLocale },
    } as unknown) as Props,
  };
};

/**
 * Self-contained controlled FAQ row. Tracks open/closed state locally so
 * each row can independently expand or collapse on click, and the
 * +/− icon mirrors that state correctly.
 */
const FaqItem = ({
  question,
  answer,
  defaultOpen,
}: {
  question: string;
  answer: string;
  defaultOpen: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <FaqRow>
      <FaqQuestion
        type="button"
        $open={open}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {question}
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

export default CurriculumPhonicsPage;

/* ============================ styled ============================ */

const Hero = styled.section`
  background: ${theme.colors.paleBeige};
  padding: 80px 32px 88px;

  @media (max-width: 992px) {
    padding: 56px 22px 56px;
  }
`;

const HeroGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 56px;
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const HeroText = styled.div`
  max-width: 620px;
`;

const Breadcrumbs = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: rgba(40, 47, 51, 0.5);
  margin-bottom: 22px;
  display: inline-flex;
  gap: 8px;
  align-items: center;

  a {
    color: inherit;
    text-decoration: none;
  }
  a:hover {
    color: ${theme.colors.black};
  }
  span[aria-hidden] {
    opacity: 0.5;
  }
`;

const HeroH1 = styled.h1`
  font-size: clamp(40px, 5.6vw, 64px);
  line-height: 1.04;
  font-weight: 900;
  letter-spacing: -0.03em;
  margin: 0 0 22px;
  color: ${theme.colors.black};
  text-wrap: balance;

  @media (max-width: 992px) {
    font-size: clamp(32px, 7vw, 48px);
  }
`;

const HeroAccent = styled.span<{ $brand: string }>`
  color: ${({ $brand }) => $brand};
`;

const HeroLede = styled.p`
  font-size: 19px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  margin: 0 0 26px;
`;

const HeroDivider = styled.div`
  height: 1px;
  background: rgba(40, 47, 51, 0.12);
  margin: 8px 0 28px;
  max-width: 520px;
`;

const HeroCtas = styled.div`
  display: flex;
  gap: 12px;
  margin: 28px 0 0;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  background: ${theme.colors.orange};
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 900;
  padding: 14px 24px;
  line-height: 1.1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 8px 20px -8px rgba(229, 131, 50, 0.6);
  transition: transform 200ms ease, background-color 200ms ease;

  &:hover {
    background: ${theme.colors.orangeDark};
    transform: translateY(-1px);
  }
`;

const GhostButton = styled.button`
  background: transparent;
  color: ${theme.colors.black};
  border: 2px solid ${theme.colors.black};
  border-radius: 10px;
  font-size: 16px;
  font-weight: 900;
  padding: 12px 22px;
  line-height: 1.1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 200ms ease, color 200ms ease;

  &:hover {
    background: ${theme.colors.black};
    color: #fff;
  }
`;

const InlineStats = styled.div`
  display: flex;
  gap: 56px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 32px;
  }
`;

const InlineStat = styled.div``;

const InlineStatN = styled.div<{ $brand: string }>`
  font-size: 44px;
  font-weight: 900;
  color: ${({ $brand }) => $brand};
  line-height: 1;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 34px;
  }
`;

const InlineStatL = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: rgba(40, 47, 51, 0.7);
  margin-top: 8px;
`;

const Section = styled.section`
  padding: 96px 32px;
  background: ${theme.colors.paleBeige};

  @media (max-width: 600px) {
    padding: 64px 22px;
  }
`;

const SectionWhite = styled(Section)`
  background: #fff;
`;

const Wrap = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const SecHead = styled.div<{ $align?: 'left' | 'center' }>`
  text-align: ${({ $align }) => $align ?? 'center'};
  max-width: 760px;
  margin: 0 auto 56px;
`;

const Eyebrow = styled.div`
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.colors.orangeDark};
`;

const SecH2 = styled.h2<{ $align?: 'left' | 'center'; $small?: boolean }>`
  font-size: ${({ $small }) =>
    $small ? 'clamp(28px, 3.4vw, 40px)' : 'clamp(32px, 4vw, 46px)'};
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.08;
  margin: 14px 0;
  color: ${theme.colors.black};
  text-wrap: balance;
  text-align: ${({ $align }) => $align ?? 'center'};
  ${({ $align }) =>
    $align === 'left' &&
    css`
      max-width: none;
    `}
`;

const SecLead = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  margin: 0;
`;

const AlignmentCard = styled.aside`
  background: #fff;
  border-radius: 20px;
  padding: 36px 40px;
  box-shadow: 0 12px 40px -16px rgba(40, 47, 51, 0.14), 0 2px 6px rgba(40, 47, 51, 0.04);

  /* Use a neutral slate for the eyebrow inside the card — the brand color
     is reserved for the check circles. */
  & > :first-child {
    color: rgba(40, 47, 51, 0.55);
  }

  @media (max-width: 600px) {
    padding: 28px 24px;
  }
`;

const AlignmentCardTitle = styled.h2`
  font-size: 22px;
  font-weight: 900;
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: ${theme.colors.black};
  margin: 12px 0 22px;
`;

const AlignmentList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const AlignmentRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(40, 47, 51, 0.09);
  font-weight: 800;
  font-size: 15.5px;
  color: ${theme.colors.black};

  &:last-child {
    border-bottom: none;
  }
`;

const AlignmentCheck = styled.span<{ $brand: string }>`
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: ${({ $brand }) => $brand};
  color: #fff;
  font-weight: 900;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ScopeGrid = styled.div`
  display: grid;
  grid-template-columns: 1.55fr 0.9fr;
  gap: 56px;
  align-items: start;
  max-width: 1080px;
  margin: 0 auto;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const ScopeBody = styled.div``;

const ScopeP = styled.p`
  font-size: 17px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.7;
  margin: 0 0 20px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const ScopeQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 4px 0 4px 24px;
  margin: 30px 0 0;
  font-size: 21px;
  font-weight: 800;
  font-style: italic;
  color: ${theme.colors.black};
  line-height: 1.45;
`;

const ScopeMeta = styled.aside`
  background: #fff;
  border-radius: 18px;
  padding: 10px 28px;
  box-shadow: 0 2px 20px 0 rgba(222, 175, 141, 0.2);

  @media (min-width: 993px) {
    position: sticky;
    top: 96px;
  }
`;

const SmRow = styled.div`
  padding: 18px 0;
  border-bottom: 1px solid rgba(40, 47, 51, 0.09);

  &:last-child {
    border-bottom: none;
  }
`;

const SmKey = styled.div`
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(40, 47, 51, 0.5);
  margin-bottom: 6px;
`;

const SmValue = styled.div`
  font-weight: 900;
  font-size: 16px;
  color: ${theme.colors.black};
  line-height: 1.3;
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.div`
  background: ${theme.colors.paleBeige};
  border-radius: 18px;
  padding: 26px 22px 24px;
  transition: transform 200ms ease, box-shadow 200ms ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 2px 20px 0 rgba(222, 175, 141, 0.2);
  }
`;

const StepNum = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: ${theme.colors.black};
  color: #fff;
  font-weight: 900;
  font-size: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
`;

const StepH3 = styled.h3`
  font-size: 20px;
  font-weight: 900;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
  color: ${theme.colors.black};
`;

const StepP = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  margin: 0;
`;

const Band = styled.section`
  background: ${theme.colors.black};
  color: #fff;
  padding: 86px 32px;

  @media (max-width: 600px) {
    padding: 64px 22px;
  }
`;

const BandGrid = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const BandCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  padding: 32px 30px;
`;

const BandIcon = styled.div<{ $variant: 'check' | 'arrow' }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 18px;
  color: #fff;
  background: ${({ $variant }) =>
    $variant === 'check' ? theme.colors.successText : theme.colors.orange};
`;

const BandH3 = styled.h3`
  font-size: 23px;
  font-weight: 900;
  letter-spacing: -0.01em;
  margin: 0 0 12px;
  color: #fff;
`;

const BandP = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.74);
  line-height: 1.55;
  margin: 0;
`;

const GradeFilter = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 42px;
`;

const GChip = styled.button<{ $active: boolean }>`
  background: ${({ $active }) => ($active ? theme.colors.black : '#fff')};
  border: 1.5px solid ${({ $active }) => ($active ? theme.colors.black : '#d9d9d9')};
  border-radius: 999px;
  padding: 9px 17px;
  font-weight: 900;
  font-size: 14px;
  color: ${({ $active }) => ($active ? '#fff' : theme.colors.black)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: border-color 200ms ease, color 200ms ease, background-color 200ms ease;

  &:hover {
    ${({ $active }) =>
      !$active &&
      css`
        border-color: ${theme.colors.orange};
        color: ${theme.colors.orangeDark};
      `}
  }
`;

const GChipN = styled.span<{ $active?: boolean }>`
  font-size: 12px;
  font-weight: 900;
  color: inherit;
  opacity: 0.6;
`;

const LessonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 38px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const LessonCard = styled.a`
  background: #fff;
  border-radius: 16px;
  padding: 24px 22px 20px;
  box-shadow: 0 2px 20px 0 rgba(222, 175, 141, 0.2);
  border: 1px solid rgba(40, 47, 51, 0.04);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  text-decoration: none;
  color: ${theme.colors.black};
  transition: transform 200ms ease, box-shadow 200ms ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(40, 47, 51, 0.14);
  }
`;

const LcTag = styled.div`
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${theme.colors.orangeDark};
  margin-bottom: 12px;
`;

const LcTitle = styled.div`
  font-size: 19px;
  font-weight: 900;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0 0 14px;
  color: ${theme.colors.black};
`;

const LcLine = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;

  strong {
    color: ${theme.colors.black};
    font-weight: 800;
  }
`;

const LcWords = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
`;

const LcWord = styled.span`
  font-weight: 900;
  font-size: 13px;
  color: ${theme.colors.black};
  background: ${theme.colors.paleBeige};
  border-radius: 7px;
  padding: 5px 9px;
`;

const LcFoot = styled.div`
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(40, 47, 51, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LcGradeBadge = styled.span`
  font-size: 11px;
  font-weight: 900;
  color: rgba(40, 47, 51, 0.5);
  background: ${theme.colors.paleBeige};
  padding: 4px 10px;
  border-radius: 999px;
`;

const LcOpen = styled.span`
  font-size: 13px;
  font-weight: 900;
  color: ${theme.colors.orangeDark};
`;

const LessonEmpty = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px;
  font-size: 16px;
  font-weight: 800;
  color: rgba(40, 47, 51, 0.7);
  background: ${theme.colors.paleBeige};
  border-radius: 16px;
`;

const BrowseNote = styled.p`
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.5);
  margin: 0 0 26px;
`;

const BrowseCta = styled.div`
  text-align: center;
`;

const Faq = styled.div`
  max-width: 820px;
  margin: 0 auto;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid rgba(40, 47, 51, 0.13);
`;

const FaqQuestion = styled.button<{ $open: boolean }>`
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  padding: 24px 0;
  font-family: inherit;
  font-weight: 900;
  font-size: 19px;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  transition: color 200ms ease;

  &::after {
    content: ${({ $open }) => ($open ? "'\\2212'" : "'+'")};
    font-size: 28px;
    font-weight: 900;
    color: ${theme.colors.orangeDark};
    line-height: 1;
    flex-shrink: 0;
  }

  &:hover {
    color: ${theme.colors.orangeDark};
  }
`;

const FaqAnswer = styled.div`
  padding: 0 0 26px;
  font-size: 16px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.65;
  max-width: 720px;
`;

const MiniGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const Mini = styled.a`
  background: #fff;
  border-radius: 16px;
  padding: 26px 24px;
  box-shadow: 0 2px 20px 0 rgba(222, 175, 141, 0.2);
  text-decoration: none;
  color: ${theme.colors.black};
  transition: transform 200ms ease, box-shadow 200ms ease;
  cursor: pointer;
  display: block;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(40, 47, 51, 0.14);
  }
`;

const MiniH3 = styled.h3`
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.01em;
  margin: 0 0 6px;
  color: ${theme.colors.black};
`;

const MiniN = styled.div`
  font-size: 13px;
  font-weight: 900;
  color: ${theme.colors.orangeDark};
  margin-bottom: 12px;
`;

const MiniP = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  margin: 0;
`;
